import React, { useState } from 'react';
import { ZoomIn, ZoomOut, X } from 'lucide-react';

interface ImageZoomProps {
  src: string;
  alt: string;
}

export default function ImageZoom({ src, alt }: ImageZoomProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => {
    if (scale < 3) setScale(prev => prev + 0.5);
  };

  const handleZoomOut = () => {
    if (scale > 1) setScale(prev => prev - 0.5);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - startPosition.x,
        y: e.clientY - startPosition.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setStartPosition({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.touches[0].clientX - startPosition.x,
        y: e.touches[0].clientY - startPosition.y
      });
    }
  };

  return (
    <div className="relative w-full h-full">
      <img
        src={src}
        alt={alt}
        className="w-full h-[400px] md:h-[600px] object-cover cursor-zoom-in"
        onClick={() => setIsZoomed(true)}
      />

      {isZoomed && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          <div
            className="relative w-full h-full overflow-hidden"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
          >
            <img
              src={src}
              alt={alt}
              className="absolute transition-transform duration-200"
              style={{
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                transformOrigin: 'center',
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          </div>

          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-4 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <button
              onClick={handleZoomOut}
              disabled={scale <= 1}
              className="p-2 text-white disabled:opacity-50"
              aria-label="Reducir zoom"
            >
              <ZoomOut size={20} />
            </button>
            <span className="text-white font-medium">{Math.round(scale * 100)}%</span>
            <button
              onClick={handleZoomIn}
              disabled={scale >= 3}
              className="p-2 text-white disabled:opacity-50"
              aria-label="Aumentar zoom"
            >
              <ZoomIn size={20} />
            </button>
          </div>

          <button
            onClick={() => {
              setIsZoomed(false);
              setScale(1);
              setPosition({ x: 0, y: 0 });
            }}
            className="fixed top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors"
            aria-label="Cerrar zoom"
          >
            <X size={24} />
          </button>
        </div>
      )}
    </div>
  );
}